import { ChangeDetectionStrategy, Component, Input, OnInit, TemplateRef, ViewChild, output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { pick } from 'lodash-es';

import { SpecialValidators } from 'ish-shared/forms/validators/special-validators';

import { Wishlist } from '../../models/wishlist/wishlist.model';

/**
 * The Wishlist Preferences Dialog shows the modal to create/edit a wishlist.
 *
 * @example
 * <ish-account-wishlist-preferences-dialog
    [wishlist]="wishlist"
    (submitWishlist)="createWishlist($event)">
   </ish-account-wishlist-preferences-dialog>
 */
@Component({
  selector: 'ish-wishlist-preferences-dialog',
  templateUrl: './wishlist-preferences-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WishlistPreferencesDialogComponent implements OnInit {
  /**
   * Predefined wishlist to fill the form with, if there is no wishlist a new wishlist will be created
   */
  @Input() wishlist: Wishlist;

  @Input() isEdit: boolean;

  @Input() modalTitle: string;

  /**
   * Emits the data of the new wishlist to create.
   */
  readonly submitWishlist = output<Wishlist>();

  wishListForm = new FormGroup({});
  model: Partial<Wishlist> = { preferred: false };
  fields: FormlyFieldConfig[];

  /**
   *  A reference to the current modal.
   */
  modal: NgbModalRef;

  // localization keys, default = for new

  primaryButton = 'account.wishlists.new_wishlist_form.create_button.text';
  wishlistTitle = 'account.wishlists.choose_wishlist.new_wishlist_name.initial_value';
  modalHeader = 'account.wishlists.new_wishlist_dialog.header';

  @ViewChild('modal') modalTemplate: TemplateRef<unknown>;

  constructor(private ngbModal: NgbModal) {}

  ngOnInit() {
    this.fields = this.getFieldConfig(this.modalTitle);
    if (this.isEdit || this.wishlist) {
      this.primaryButton = 'account.wishlists.edit_wishlist_form.save_button.text';
      this.modalHeader = 'account.wishlist.list.edit';
    }
  }

  /** Emits the wishlist data, when the form was valid. */
  submitWishlistForm() {
    if (this.wishListForm.valid) {
      this.submitWishlist.emit({
        id: !this.wishlist ? this.model.title : this.wishlistTitle,
        preferred: this.model.preferred,
        title: this.model.title,
        public: false,
      });
      this.hide();
    }
  }

  /** Opens the modal. */
  show() {
    this.wishListForm.reset();
    if (!this.wishlist) {
      this.model = { preferred: false };
    } else {
      this.model = pick(this.wishlist, 'title', 'preferred');
    }
    this.modal = this.ngbModal.open(this.modalTemplate, { ariaLabelledBy: 'wishlist-preferences-modal-title' });
  }

  /** Close the modal. */
  hide() {
    if (this.modal) {
      this.modal.close();
    }
  }

  getFieldConfig(title?: string) {
    const baseField = {
      key: 'title',
      type: 'ish-text-input-field',
      props: {
        required: true,
        label: 'account.wishlists.wishlist_form.name.label',
        hideRequiredMarker: true,
        maxLength: 35,
      },
      validators: {
        validation: [SpecialValidators.noHtmlTags],
      },
      validation: {
        messages: {
          required: 'account.wishlists.wishlist_form.name.error.required',
          noHtmlTags: 'account.name.error.forbidden.html.chars',
        },
      },
    };

    const preferredField = {
      key: 'preferred',
      type: 'ish-checkbox-field',
      props: {
        label: 'account.wishlists.wishlist_form.preferred.label',
        fieldClass: 'offset-md-4 col-md-8 d-flex align-items-center',
        tooltip: {
          link: 'account.wishlists.wishlist_form.preferred.tooltip.linktext',
          text: 'account.wishlists.wishlist_form.preferred.tooltip.content',
          title: 'account.wishlists.wishlist_form.preferred.tooltip.headline',
          class: 'details-tooltip',
        },
      },
    };

    return title ? [baseField] : [baseField, preferredField];
  }
  buttonDisable(): boolean {
    const errorMessageElement = document.querySelector('.has-error');
    if (errorMessageElement) {
      return true;
    } else {
      return false;
    }
  }
}
